<template>
  <v-row class="form-step">
    <v-col>
      <div>
        <label class="label">
          Select Dimension
          <v-tooltip class="ml-2" text="A single dimension must be selected.">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                size="x-small"
                icon
                variant="outlined"
                color="#0d47a1"
              >
                <v-icon icon="fas fa-info" />
              </v-btn>
            </template> </v-tooltip
        ></label>
        <v-form fast-fail>
        <v-row>
          <v-col cols="4" class="mt-4">
            <v-select
              v-model="form.kpi.dimension_attributes.name"
              deselect-label="Can't remove this value"
              label="Select Dimension"
              :items="optionsDimension"
              item-title="text"
              item-value="value"
              :custom-label="dimensionLabel"
              variant="solo"
              hint="Select one of the available dimensions"
              persistent-hint
              class="mb-6"
              @update:modelValue="onDimensionChange"
              :rules="dimensionPresenceRule"
            >
            </v-select>
          </v-col>
        </v-row>
      </v-form>
      </div>
      <hr />
      <v-form fast-fail>
        <v-row v-if="form.aggregationLevels.length > 0" class="mb-3">
          <v-col class="mt-6 label">
            Enter weight for each aggregation level
            <v-tooltip
             style="max-width: 800px; white-space: normal"
              text="Enter values in range [0,1]. Non-normalized weights will be supported in the future."
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="x-small"
                  icon
                  variant="outlined"
                  color="#0d47a1"
                >
                  <v-icon icon="fas fa-info" />
                </v-btn>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="3"
            v-for="(level, index) in form.aggregationLevels"
            :key="level.name"
          >
            <div class="agg-level">
              <span>
                <strong>{{ capitalize(level.name) }}</strong>
                <v-text-field
                  :id="`${level.name}_dimension_weight`"
                  v-model="form.aggregationLevels[index].dimension_weight"
                  :bg-color="fieldColorCoding(level.name)"
                  type="number"
                  :placeholder="getPlaceholder(index)"
                  :readonly="
                    form.aggregationLevels[index].dimension_weight === null
                  "
                  variant="solo"
                  required
                  :rules="weightRule"
                >
                </v-text-field>
                <div
                  v-if="
                    accumulatedWeights[dimensionName] &&
                    parseFloat(accumulatedWeights[dimensionName][level.name]) >
                      0
                  "
                  style="float: right"
                >
                  <i class="text-orange">
                    (accumulated weight:
                    {{ accumulatedWeights[dimensionName][level.name] }})
                  </i>
                </div>
                <i class="text-orange" v-else>(No accumulated weight)</i>
              </span>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>

<script setup>
import { dimensionPresenceRule, weightRule, fieldColorCoding } from "@/Shared/StepsRules.vue";
import { capitalize, computed, onMounted, ref } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  accumulatedWeights: {
    type: Object,
    required: false,
    default: null,
  },
  ignoredWeightsServer: {
    type: Array,
    required: false,
    default: function () {
      return [];
    },
  },
});

const form = props.modelValue;
const dimensionName = computed(() => {
  form.kpi.dimension_attributes.name;
});

const optionsDimension = [
  { value: "economic", text: "Economic" },
  { value: "energy", text: "Energy" },
  { value: "environmental", text: "Environmental" },
  { value: "governance", text: "Governance" },
  { value: "ict", text: "ICT" },
  { value: "mobility", text: "Mobility" },
  { value: "propagation", text: "Propagation" },
  { value: "social", text: "Social" },
];

function dimensionLabel(v) {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function onDimensionChange($event) {
  // check if it in the ignored weights list to set it to null
  const selected = $event;
  const items = props.ignoredWeightsServer.filter(
    (e) => e.dimension === selected
  );
  let ignored = [];
  let notIgnored = [];
  // find dimension in aggregation levels
  const levels = form.aggregationLevels;
  if (items.length > 0) {
    for (let i = 0; i < items.length; i++) {
      const record = levels.filter((e) => e.name === items[i].level)[0];
      ignored.push(record.name);
      record["dimension_weight"] = null;
    }
  }
  notIgnored = levels.filter((e) => !ignored.includes(e.name));
  for (let i = 0; i < notIgnored.length; i++) {
    const record = levels.filter((e) => e.name === notIgnored[i].name)[0];
    record["dimension_weight"] = "";
  }
}

function getPlaceholder(index) {
  if (form.aggregationLevels[index].dimension_weight === null)
    return "Weight is ignored";
  return "Enter weight";
}
onMounted(() => {
  // for (let i = 0; i < this.form.aggregationLevels.length; i++) {
  //   this.ignoreWeightsForLevel.push(false)
  //   this.form.aggregationLevels[i].dimension_weight = ""
  // }
  // ---- new from server ignored weights
  for (let i = 0; i < props.ignoredWeightsServer.length; i++) {
    const item = props.ignoredWeightsServer[i];
    const alItem = form.aggregationLevels.filter(
      (e) => e.name === item.level
    )[0];
    if (
      alItem &&
      form.kpi.dimension_attributes.name.toLowerCase() === item.dimension
    )
      alItem[`dimension_weight`] = null;
  }

  // find if there are null but something has changed in ignore weights
  // e.g. saved a kpi when mobility-building was being ignored
  // but the user changes this preference so it has to be '' from null
  const aggLevels = form.aggregationLevels;
  const dimension = form.kpi.dimension_attributes["name"];
  for (let i = 0; i < aggLevels.length; i++) {
    if (aggLevels[i]["dimension_weight"] === null) {
      const name = aggLevels[i]["name"];
      const item = props.ignoredWeightsServer.filter(
        (e) => e.dimension === dimension && e.level === name
      )[0];
      if (!item) aggLevels[i]["dimension_weight"] = "";
    }
  }
});
</script>

<style scoped>
strong {
  font-size: medium;
}
.label {
  font-size: medium;
}
</style>
